import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from './utils/api';

function SettingsListCreation() {
  const navigate = useNavigate();
  const [activeList, setActiveList] = useState('customer_type');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState('');
  const [parentFilter, setParentFilter] = useState('');
  const [doorTypes, setDoorTypes] = useState([]);

  useEffect(() => {
    fetchItems();
  }, [activeList, parentFilter]);

  useEffect(() => {
    // Fetch door types for work area parent filtering
    if (activeList === 'work_type') {
      fetchDoorTypes();
    }
  }, [activeList]);

  const fetchDoorTypes = async () => {
    try {
      const res = await apiFetch('/api/custom-lists?list_type=door_type');
      const data = await res.json();
      setDoorTypes(data);
    } catch (err) {
      console.error('Error fetching door types:', err);
    }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      let url = `/api/custom-lists?list_type=${activeList}`;
      if (activeList === 'work_type' && parentFilter) {
        url += `&parent_value=${encodeURIComponent(parentFilter)}`;
      }
      const res = await apiFetch(url);
      const data = await res.json();
      setItems(data);
    } catch (err) {
      console.error('Error fetching custom lists:', err);
      setItems([]);
    }
    setLoading(false);
  };

  const addItem = async () => {
    if (!newItem.trim()) return;

    try {
      const body = {
        list_type: activeList,
        list_value: newItem.trim(),
        display_order: items.length
      };
      if (activeList === 'work_type' && parentFilter) {
        body.parent_value = parentFilter;
      }
      const res = await apiFetch('/api/custom-lists', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (res.ok) {
        setNewItem('');
        fetchItems(); // Reload list
      } else {
        alert('Failed to add item');
      }
    } catch (err) {
      console.error('Error adding item:', err);
      alert('Error adding item');
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm('Delete this item?')) return;

    try {
      const res = await apiFetch(`/api/custom-lists/${id}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        fetchItems(); // Reload list
      } else {
        alert('Failed to delete item');
      }
    } catch (err) {
      console.error('Error deleting item:', err);
      alert('Error deleting item');
    }
  };

  const listTypes = [
    { id: 'customer_type', label: 'Customer Types', icon: '👥' },
    { id: 'contact_role', label: 'Contact Roles', icon: '👤' },
    { id: 'site_type', label: 'Site Types', icon: '🏢' },
    { id: 'job_status', label: 'Job Statuses', icon: '📊' },
    { id: 'job_category', label: 'Work Types', icon: '📁' },
    { id: 'door_type', label: 'Equipment Types', icon: '🚪' },
    { id: 'work_type', label: 'Work Types', icon: '🔧', hasParent: true },
    { id: 'call_type', label: 'Call Types', icon: '📞' },
    { id: 'call_priority', label: 'Call Priorities', icon: '🔥' },
    { id: 'response_time', label: 'Response Times', icon: '⏱️' }
  ];

  const currentListType = listTypes.find(t => t.id === activeList);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* List Type Selector */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 h-fit">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Select List
            </h2>
            <nav className="space-y-1">
              {listTypes.map(type => (
                <button
                  key={type.id}
                  onClick={() => setActiveList(type.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition flex items-center space-x-2 ${
                    activeList === type.id
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span>{type.icon}</span>
                  <span>{type.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* List Content */}
          <div className="md:col-span-3">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                <span>{currentListType?.icon}</span>
                <span>{currentListType?.label}</span>
              </h2>

              {/* Parent filter for Work Types */}
              {activeList === 'work_type' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Equipment Type</label>
                  <select
                    value={parentFilter}
                    onChange={(e) => setParentFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Equipment Types</option>
                    {doorTypes.map(dt => (
                      <option key={dt.id} value={dt.list_value}>{dt.list_value}</option>
                    ))}
                  </select>
                  {!parentFilter && (
                    <p className="text-sm text-gray-500 mt-2">Select an equipment type to see/add work areas specific to that equipment.</p>
                  )}
                </div>
              )}

              {/* Add New Item Form */}
              <div className="mb-6 flex gap-2">
                <input
                  type="text"
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addItem()}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder={`Add new ${currentListType?.label.toLowerCase().replace(' types', '').replace(' roles', '').replace(' statuses', '').replace(' categories', '').replace(' priorities', '').replace(' times', '')}...`}
                />
                <button
                  onClick={addItem}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
                >
                  Add
                </button>
              </div>

              {/* List Items */}
              <div className="space-y-2">
                {items.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No items yet. Add your first {currentListType?.label.toLowerCase().replace(' types', '').replace(' roles', '').replace(' statuses', '').replace(' categories', '').replace(' priorities', '').replace(' times', '')}.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {items.map((item) => (
                      <div
                        key={item.id}
                        className="px-3 py-1.5 bg-gray-100 border border-gray-200 rounded-full flex items-center space-x-2"
                      >
                        <span className="text-sm font-medium text-gray-700">{item.list_value}</span>
                        <button
                          onClick={() => removeItem(item.id)}
                          className="text-gray-400 hover:text-red-600 transition"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsListCreation;
